import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import urljoin from 'url-join';
import cn from 'classnames';

import Layout from '@/components/Layout/Vanilla';
import { fetchPage, gql } from '@/lib/graphcms';
import Card from '@/components/blog/card';
import Heading from '@/components/Heading';
import Pagination from '@/components/blog/pagination';
import Badge from '@/components/blog/badge';
import SlackCard from '@/components/blog/slack-card';
import EnterpriseCard from '@/components/blog/enterprise-card';
import styles from '@/styles/pages/blog-index.module.css';
import { Page, Post } from '@/types';
import CardsContainer from '@/components/blog/cards-container';
import FeaturedPost, {
  FeaturedPostProps,
} from '@/components/blog/featured-post';
import { getPagination } from '@/utils/blog';
import { resourceCards } from '@/utils/data/resource-cards';
import ResourceCard from '@/components/blog/resource-card';
import { defaultSeoUrl } from '../../../next-seo.config';

type BlogProps = {
  page: Page;
  blogPostsConnection: {
    blogPosts: {
      node: Post;
    }[];
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    aggregate: {
      count: number;
    };
  };
  featuredPost: FeaturedPostProps;
  categories: {
    title: string;
    slug: string;
  }[];
  currentPage: number;
  isFirstPage: boolean;
  preview: boolean;
};

// The limit is 10 because we remove the featured post from the list
const limit = 10;

export default function Blog({
  page,
  blogPostsConnection: { blogPosts, aggregate, pageInfo },
  featuredPost,
  categories,
  currentPage,
  isFirstPage,
  preview,
}: BlogProps) {
  const router = useRouter();

  const pageUrl = urljoin(defaultSeoUrl, router.asPath);
  const pageTitle = !isFirstPage
    ? `Page ${currentPage} | ${page.title}`
    : page.title;

  return (
    <>
      <Head>
        {pageInfo.hasPreviousPage && (
          <link rel="prev" href={`/blog?page=${currentPage - 1}`} />
        )}
        {pageInfo.hasNextPage && (
          <link rel="next" href={`/blog?page=${currentPage + 1}`} />
        )}
        {!isFirstPage && <link rel="canonical" href={pageUrl} />}
      </Head>

      <Layout
        preview={preview}
        seo={{
          ...page,
          meta: {
            ...page.meta,
            title: pageTitle,
          },
        }}
      >
        <div className="mx-auto max-w-6xl px-6">
          <Heading>Blog</Heading>

          {categories.length > 0 && (
            <div className="mt-6 space-y-3 lg:space-y-0">
              {categories.map((category) => (
                <Badge
                  key={category.slug}
                  slug={`/blog/tag/${category.slug}`}
                  title={category.title}
                  variant="default"
                  className="mr-3 lg:mr-2"
                />
              ))}
            </div>
          )}

          <div className="mt-10">
            <FeaturedPost {...featuredPost} />
          </div>

          <CardsContainer className="mt-10 lg:mt-20">
            {blogPosts.map(({ node }) => (
              <Card key={node.id} post={node} />
            ))}
          </CardsContainer>

          <div className="mt-20">
            <Pagination
              currentPage={currentPage}
              totalCount={aggregate.count}
              pageSize={limit}
              onPageChange={(pageNumber) => {
                router.push(`/blog?page=${pageNumber}`);
              }}
            />
          </div>

          <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {resourceCards.map((item) => (
              <ResourceCard key={item.href} {...item} />
            ))}
          </div>

          <div className={cn(`mt-20 mb-12 lg:mb-36`, styles.cardsFooter)}>
            <SlackCard />
            <EnterpriseCard />
          </div>
        </div>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  preview = false,
  params,
  res,
  query,
}) => {
  const { page, offset } = getPagination({
    queryPage: query.page,
    limit,
  });

  const {
    blogPostsConnection,
    page: pageData,
    featuredPosts,
    categories,
  } = (await fetchPage(
    gql`
      fragment categoryData on Category {
        id
        slug
        title
      }

      fragment blogPostData on BlogPostEdge {
        node {
          id
          slug
          title
          subTitle
          coverImage {
            ...assetDataXL
          }
          authors {
            ...authorData
          }
          categories {
            ...categoryData
          }
          publishedOn
        }
      }

      query ($limit: Int!, $offset: Int!) {
        blogPostsConnection(
          orderBy: publishedOn_DESC
          first: $limit
          skip: $offset
        ) {
          blogPosts: edges {
            ...blogPostData
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          aggregate {
            count
          }
        }
        featuredPosts: blogPosts(
          orderBy: publishedOn_DESC
          where: { featured: true }
          first: 1
        ) {
          title
          slug
          subTitle
          coverImage {
            alt
            url
          }
          categories {
            id
            title
            slug
          }
          publishedOn
          authors {
            name
            photo {
              url
              alt
            }
          }
        }
        categories(where: { blogPosts_some: {} }) {
          title
          slug
        }
        page: page(where: { slug: "blog" }) {
          ...pageData
        }
      }
    `,
    {
      preview,
      variables: {
        slug: params?.slug,
        limit,
        offset,
      },
    },
  )) || [];

  // If the page param is bigger than the total number of pages, redirect to /blog
  const totalNumberOfPages = Math.ceil(
    blogPostsConnection.aggregate.count / limit,
  );

  if (page > totalNumberOfPages) {
    return {
      redirect: {
        destination: `/blog`,
        permanent: false,
      },
    };
  }

  const isFirstPage = page === 1;
  const featuredPost = isFirstPage
    ? featuredPosts[0]
    : blogPostsConnection.blogPosts[0].node;

  // find the featured post based on the slug and remove it from the list
  const featuredPostIndex = blogPostsConnection.blogPosts.findIndex(
    (post: { node: Post }) => post.node.slug === featuredPost.slug,
  );

  if (featuredPostIndex !== -1) {
    blogPostsConnection.blogPosts.splice(featuredPostIndex, 1);
  }

  res.setHeader(`Cache-Control`, `s-maxage=300, stale-while-revalidate`);

  return {
    props: {
      preview,
      page: pageData,
      blogPostsConnection,
      currentPage: page,
      featuredPost,
      categories,
      isFirstPage,
    },
  };
};
